import React, { useState , useEffect, useCallback, useRef } from 'react';
import { FileUpload } from '../js/upload';

import UploadEditHeaders from './hsms/modals/upload_edit_headers';
import UploadConfirmDataValidation from './hsms/modals/upload_confirm_data_validation';

import { getProcessFeedback, clearProcessFeedback, continueProcess } from '../js/Helpers';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileUpload, faSquare, faCheckSquare, faCheckCircle, faCheck, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import { faTimesCircle } from '@fortawesome/free-regular-svg-icons';

const UPLOAD_PHASES = {
	IDLE: 'idle',
	UPLOADING_FILE: 'uploading_file',
	PREPARING_FILE: 'preparing_file',
	WAITING_FOR_INITIAL_FEEDBACK: 'waiting_for_initial_feedback',
	AWAITING_HEADER_CONFIRMATION: 'awaiting_header_confirmation',
	VALIDATING_DATA: 'validating_data',
	AWAITING_VALIDATION_REVIEW: 'awaiting_validation_review',
	IMPORTING_ROWS: 'importing_rows',
	SUCCESS: 'success',
	FAILURE: 'failure'
};

const INITIAL_UPLOAD_FLOW = {
	phase: UPLOAD_PHASES.IDLE,
	label: '',
	detail: '',
	percent: 0,
	percentMode: 'real',
	stepIndex: 0,
	stepCount: 5,
	canCancel: false,
	actionLabel: null,
	actionType: null,
	lastBackendProgress: null
};

const PHASE_CONFIG = {
	[UPLOAD_PHASES.IDLE]: {
		label: '',
		detail: '',
		stepIndex: 0,
		percentMode: 'real',
		canCancel: false,
		actionLabel: null,
		actionType: null
	},
	[UPLOAD_PHASES.UPLOADING_FILE]: {
		label: 'Uploading your file',
		detail: 'Sending your spreadsheet so we can start checking it.',
		stepIndex: 1,
		percentMode: 'real',
		canCancel: false,
		actionLabel: null,
		actionType: null
	},
	[UPLOAD_PHASES.PREPARING_FILE]: {
		label: 'Reading your file',
		detail: 'We’re opening your spreadsheet and getting the columns ready.',
		stepIndex: 2,
		percentMode: 'simulated',
		canCancel: true,
		actionLabel: null,
		actionType: null
	},
	[UPLOAD_PHASES.WAITING_FOR_INITIAL_FEEDBACK]: {
		label: 'Preparing column review',
		detail: 'Your file uploaded successfully. We’re still preparing the column confirmation step.',
		stepIndex: 2,
		percentMode: 'paused',
		canCancel: true,
		actionLabel: null,
		actionType: null
	},
	[UPLOAD_PHASES.AWAITING_HEADER_CONFIRMATION]: {
		label: 'Columns need your review',
		detail: 'We found your column names. Please confirm which columns match the needed data.',
		stepIndex: 3,
		percentMode: 'paused',
		canCancel: true,
		actionLabel: 'Confirm Columns',
		actionType: 'confirm_headers'
	},
	[UPLOAD_PHASES.VALIDATING_DATA]: {
		label: 'Checking your data',
		detail: 'We’re scanning your rows for anything that may need attention.',
		stepIndex: 4,
		percentMode: 'simulated',
		canCancel: true,
		actionLabel: null,
		actionType: null
	},
	[UPLOAD_PHASES.AWAITING_VALIDATION_REVIEW]: {
		label: 'Some rows need review',
		detail: 'We found possible issues in your file. You can review them before continuing.',
		stepIndex: 4,
		percentMode: 'paused',
		canCancel: true,
		actionLabel: 'Review Issues',
		actionType: 'review_validation'
	},
	[UPLOAD_PHASES.IMPORTING_ROWS]: {
		label: 'Importing your data',
		detail: 'We’re adding your rows now. This may take a few minutes for larger files.',
		stepIndex: 5,
		percentMode: 'real',
		canCancel: false,
		actionLabel: null,
		actionType: null
	},
	[UPLOAD_PHASES.SUCCESS]: {
		label: 'Upload complete',
		detail: 'Your data has been successfully imported.',
		stepIndex: 5,
		percentMode: 'real',
		canCancel: false,
		actionLabel: null,
		actionType: null
	},
	[UPLOAD_PHASES.FAILURE]: {
		label: 'Upload failed',
		detail: 'We couldn’t finish the upload. Please check the file format and try again.',
		stepIndex: 0,
		percentMode: 'real',
		canCancel: false,
		actionLabel: null,
		actionType: null
	}
};

const clampPercent = (value) => Math.max(0, Math.min(100, Math.floor(value || 0)));

const mapRange = (value, inMin, inMax, outMin, outMax) => {
	if(inMax === inMin) return outMin;

	const ratio = (value - inMin) / (inMax - inMin);
	return outMin + ratio * (outMax - outMin);
};

const getImportPercentFromBackend = (progress = 0) => {
	return clampPercent(mapRange(progress, 0, 1, 78, 99));
};

const getPausedPercentForPhase = (phase) => {
	if(phase === UPLOAD_PHASES.WAITING_FOR_INITIAL_FEEDBACK) return 28;
	if(phase === UPLOAD_PHASES.AWAITING_HEADER_CONFIRMATION) return 40;
	if(phase === UPLOAD_PHASES.AWAITING_VALIDATION_REVIEW) return 72;
	return 0;
};

const getSimulationBounds = (phase) => {
	if(phase === UPLOAD_PHASES.PREPARING_FILE) return {start: 8, end: 28};
	if(phase === UPLOAD_PHASES.VALIDATING_DATA) return {start: 55, end: 72};
	return {start: 0, end: 100};
};

const buildUploadFlow = (phase, overrides = {}) => {
	return {
		...INITIAL_UPLOAD_FLOW,
		...(PHASE_CONFIG[phase] || PHASE_CONFIG[UPLOAD_PHASES.IDLE]),
		phase,
		...overrides
	};
};

const getStepperSteps = (phase) => {
	const labels = ['Upload file', 'Read columns', 'Confirm columns', 'Check data', 'Import rows'];

	const statusMap = {
		[UPLOAD_PHASES.IDLE]: ['pending', 'pending', 'pending', 'pending', 'pending'],
		[UPLOAD_PHASES.UPLOADING_FILE]: ['current', 'pending', 'pending', 'pending', 'pending'],
		[UPLOAD_PHASES.PREPARING_FILE]: ['complete', 'current', 'pending', 'pending', 'pending'],
		[UPLOAD_PHASES.WAITING_FOR_INITIAL_FEEDBACK]: ['complete', 'current', 'pending', 'pending', 'pending'],
		[UPLOAD_PHASES.AWAITING_HEADER_CONFIRMATION]: ['complete', 'complete', 'current', 'pending', 'pending'],
		[UPLOAD_PHASES.VALIDATING_DATA]: ['complete', 'complete', 'complete', 'current', 'pending'],
		[UPLOAD_PHASES.AWAITING_VALIDATION_REVIEW]: ['complete', 'complete', 'complete', 'needs_review', 'pending'],
		[UPLOAD_PHASES.IMPORTING_ROWS]: ['complete', 'complete', 'complete', 'complete', 'current'],
		[UPLOAD_PHASES.SUCCESS]: ['complete', 'complete', 'complete', 'complete', 'complete'],
		[UPLOAD_PHASES.FAILURE]: ['pending', 'pending', 'pending', 'pending', 'pending']
	};

	const statuses = statusMap[phase] || statusMap[UPLOAD_PHASES.IDLE];

	return labels.map((label, index) => ({
		label,
		status: statuses[index]
	}));
};

const getFlowStatusMessage = (uploadFlow) => {
	if(uploadFlow.phase === UPLOAD_PHASES.UPLOADING_FILE) {
		return `${uploadFlow.percent}% uploaded`;
	}

	if(uploadFlow.phase === UPLOAD_PHASES.WAITING_FOR_INITIAL_FEEDBACK) {
		return 'Waiting on the server';
	}

	if(uploadFlow.percentMode === 'paused') {
		return 'Waiting for you';
	}

	if(uploadFlow.phase === UPLOAD_PHASES.IMPORTING_ROWS) {
		return `${uploadFlow.percent}% complete`;
	}

	if(uploadFlow.phase === UPLOAD_PHASES.SUCCESS) {
		return 'Complete';
	}

	return 'Still working...';
};

const getDisplayFileName = (file, uploadTitle) => {
	if(file?.name) return file.name;
	return `${uploadTitle} upload`;
};

const INITIAL_FEEDBACK_RETRY_DELAY_MS = 1500;
const INITIAL_FEEDBACK_MAX_RETRIES = 20;
const LONG_RUNNING_STATUS_MESSAGES = [
	'Still running. Larger spreadsheets can take a few minutes.',
	'We’re still processing this file. No action is needed right now.',
	'Still working through the rows. We’ll update this as soon as the next step finishes.',
	'This is taking a bit, but the upload is still active.'
];

const isTruthyProcessFlag = (value) => value === true || value === 1 || value === '1';

const isValidXlsxFile = (file) => {
	if(!file?.name) return false;
	return /\.xlsx$/i.test(file.name);
};

const normalizeProcessPercent = (progress = 0) => {
	const numeric_progress = Number(progress) || 0;
	return clampPercent(numeric_progress > 1 ? numeric_progress : numeric_progress * 100);
};

const buildInitialScheduleProgress = (schedule_ids, schedules, existing_progress = {}) => {
	return schedule_ids.reduce((results, schedule_version_id) => {
		const schedule = schedules.find(schedule_info => `${schedule_info.schedule_version_id}` === `${schedule_version_id}`);
		const existing = existing_progress[schedule_version_id] || {};

		results[schedule_version_id] = {
			schedule_version_id,
			schedule_name: schedule?.schedule_name || `Schedule ${schedule_version_id}`,
			schedule_date: schedule?.time_stamp || '',
			progress_id: existing.progress_id || null,
			percent: existing.percent || 0,
			status: existing.status || 'queued',
			statusLabel: existing.statusLabel || 'Waiting to start'
		};

		return results;
	}, {});
};

const getScheduleProgressFromProcess = (process, fallback_progress) => {
	const failure = isTruthyProcessFlag(process.failure);
	const is_done = isTruthyProcessFlag(process.is_done);
	const has_started = isTruthyProcessFlag(process.has_started);
	const percent = failure ? normalizeProcessPercent(process.progress) : (is_done ? 100 : normalizeProcessPercent(process.progress));

	if(failure) {
		return {
			...fallback_progress,
			progress_id: process.progress_id,
			percent,
			status: 'failure',
			statusLabel: 'Failed'
		};
	}

	if(is_done) {
		return {
			...fallback_progress,
			progress_id: process.progress_id,
			percent: 100,
			status: 'complete',
			statusLabel: 'Complete'
		};
	}

	if(has_started) {
		return {
			...fallback_progress,
			progress_id: process.progress_id,
			percent,
			status: 'running',
			statusLabel: `${percent}% complete`
		};
	}

	return {
		...fallback_progress,
		progress_id: process.progress_id,
		percent: Math.max(fallback_progress.percent || 0, percent),
		status: 'queued',
		statusLabel: 'Waiting to start'
	};
};

const getUploadStatusNote = (uploadFlow, heartbeatTick) => {
	if(uploadFlow.phase === UPLOAD_PHASES.WAITING_FOR_INITIAL_FEEDBACK) {
		return 'We’re still checking for the column confirmation step.';
	}

	if(uploadFlow.percentMode === 'paused') {
		return 'This upload is waiting on your review.';
	}

	if(heartbeatTick > 0 && (uploadFlow.phase === UPLOAD_PHASES.VALIDATING_DATA || uploadFlow.phase === UPLOAD_PHASES.IMPORTING_ROWS || uploadFlow.phase === UPLOAD_PHASES.PREPARING_FILE)) {
		return LONG_RUNNING_STATUS_MESSAGES[(heartbeatTick - 1) % LONG_RUNNING_STATUS_MESSAGES.length];
	}

	return 'We’ll keep updating this automatically as each step finishes.';
};

export default function UploadModal({schoolInfo, scheduleInfo, uploadTitle, uploadAPIName, addReplaceBool = false, closeModal, handlePullNewData,}) {
	/////////////////
	///// STATE /////
	/////////////////
	const [currentScreen, setCurrentScreen] = useState(1);

	const [file, setFile] = useState({name:null});
	const [fileUpload, setFileUpload] = useState(null);

	const [checkProcessProgressCompleted, setCheckProcessProgressCompleted] = useState(false);

	const [uploadType, setUploadType] = useState(null);
	const [selectedSchedules, setSelectedSchedules] = useState([]);

	const [currentProgressID, setCurrentProgressID] = useState(null);
	const [isUploading, setIsUploading] = useState(false);
	const [uploadComplete, setUploadComplete] = useState(false);
	const [fileValidationError, setFileValidationError] = useState(null);

	const [, setUploadState] = useState(null);
	const [uploadFlow, setUploadFlow] = useState(INITIAL_UPLOAD_FLOW);
	const [headersConfirmed, setHeadersConfirmed] = useState(false);
	const [validationReviewed, setValidationReviewed] = useState(false);
	const [hasSeenImportProgress, setHasSeenImportProgress] = useState(false);
	const [heartbeatTick, setHeartbeatTick] = useState(0);
	const [scheduleProgress, setScheduleProgress] = useState({});
	
	const [feedbackData, setFeedbackData] = useState(null);
	const [fileHeaders, setFileHeaders] = useState([]);
	
	const [showHeadersFeedback, setShowHeadersFeedback] = useState(false);
	const [showUploadDataValidationFeedback, setShowUploadDataValidationFeedback] = useState(false);
	const selectedSchedulesRef = useRef([]);
	const fileHeadersRef = useRef([]);
	const headersConfirmedRef = useRef(false);
	const validationReviewedRef = useRef(false);
	const hasSeenImportProgressRef = useRef(false);
	const awaitingFirstFeedbackRef = useRef(false);
	const initialFeedbackRetryCountRef = useRef(0);
	const initialFeedbackRetryTimeoutRef = useRef(null);
	const fileUploadRef = useRef(null);
	const handleCheckUploadRef = useRef(null);
	const checkTokenRef = useRef(0);
	const multiScheduleCompletionHandledRef = useRef(false);

	const subscriptionRestrictedUploads = ['studentdata', 'studentreqdata'];
	const uploadRequiresPaidSubscription = subscriptionRestrictedUploads.includes(uploadAPIName);
	const scheduleYearInfo = scheduleInfo?.schedule_years?.find(
		(schedule_year) => `${schedule_year.schedule_year_id}` === `${schoolInfo.current_schedule_year_id}`
	);
	const hasPaidSubscriptionForScheduleYear = scheduleYearInfo ? (`${scheduleYearInfo.num_seats}` !== '0') : false;
	const blockedByMissingSubscription = uploadRequiresPaidSubscription && !hasPaidSubscriptionForScheduleYear;
	const isCurrentAppScheduleYear = (scheduleInfo.app_current_schedule_year_id === schoolInfo.current_schedule_year_id);
	const schedules_from_current_schedule_year = scheduleInfo.schedules.filter(schedule => schedule.data_version_id === schoolInfo.current_data_version_id);
	const schedule_version_ids_from_current_schedule_year = schedules_from_current_schedule_year.map(schedule => schedule.schedule_version_id);
	const selected_schedules_string = schedule_version_ids_from_current_schedule_year.join(',');
	const selected_upload_schedules_string = selectedSchedules.join(',');
	const show_multi_schedule_progress = uploadAPIName === 'studentreqdata' && selectedSchedules.length > 1;
	const schedule_progress_rows = selectedSchedules.map(schedule_version_id => scheduleProgress[schedule_version_id] || buildInitialScheduleProgress([schedule_version_id], schedules_from_current_schedule_year)[schedule_version_id]);

	/////////////////////
	///// FUNCTIONS /////
	/////////////////////
	const setFlowPhase = useCallback((phase, overrides = {}) => {
		setUploadFlow(current => buildUploadFlow(phase, {
			percent: current.percent,
			lastBackendProgress: current.lastBackendProgress,
			...overrides
		}));
	}, []);

	const resetUploadFlowState = useCallback(() => {
		setUploadFlow(INITIAL_UPLOAD_FLOW);
		setHeadersConfirmed(false);
		setValidationReviewed(false);
		setHasSeenImportProgress(false);
		setHeartbeatTick(0);
		setScheduleProgress({});
		awaitingFirstFeedbackRef.current = false;
		multiScheduleCompletionHandledRef.current = false;
		initialFeedbackRetryCountRef.current = 0;
		if(initialFeedbackRetryTimeoutRef.current) {
			clearTimeout(initialFeedbackRetryTimeoutRef.current);
			initialFeedbackRetryTimeoutRef.current = null;
		}
		headersConfirmedRef.current = false;
		validationReviewedRef.current = false;
		hasSeenImportProgressRef.current = false;
	}, []);

	const openPausedAction = () => {
		if(uploadFlow.actionType === 'confirm_headers') {
			setShowHeadersFeedback(true);
		}
		else if(uploadFlow.actionType === 'review_validation') {
			setShowUploadDataValidationFeedback(true);
		}
	};

	const onFileChange = (e) => {
		const next_file = e.target.files[0];
		
		setIsUploading(false);
		setUploadComplete(false);
		setUploadState(null);
		resetUploadFlowState();

		if(!next_file) {
			setFile({name:null});
			return;
		}

		if(!isValidXlsxFile(next_file)) {
			setFile({name:null});
			setFileValidationError('Please upload an Excel .xlsx file. Other spreadsheet formats will not import correctly.');
			setCurrentScreen(1);
			e.target.value = '';
			return;
		}

		setFileValidationError(null);
		setFile(next_file);
		setCurrentScreen(2);
	};

	const showSuccessScreen = useCallback(() => {
		awaitingFirstFeedbackRef.current = false;
		initialFeedbackRetryCountRef.current = 0;
		setCurrentScreen(4);
		handlePullNewData();
		localStorage.removeItem('upload_headers');
	}, [handlePullNewData]);

	const removeFile = useCallback((screen = 1) => {
		checkTokenRef.current += 1;
		if(fileUploadRef.current) fileUploadRef.current.cancelCheck();
		setCurrentProgressID(null);
		setFile({name:null});
		setFileHeaders([]);
		setIsUploading(false);
		setUploadComplete(false);
		setUploadState(null);
		setFeedbackData(null);
		setFileValidationError(null);
		setCurrentScreen(screen);
		localStorage.removeItem('upload_headers');
		resetUploadFlowState();
	}, [resetUploadFlowState]);

	const handleProcessingEvent = useCallback((event) => {
		const backendProgress = typeof event.progress === 'number' ? event.progress : 0;

		setCurrentScreen(3);
		setUploadComplete(false);

		if(validationReviewedRef.current || hasSeenImportProgressRef.current) {
			hasSeenImportProgressRef.current = true;
			setHasSeenImportProgress(true);
			setIsUploading(true);
			setFlowPhase(UPLOAD_PHASES.IMPORTING_ROWS, {
				percent: getImportPercentFromBackend(backendProgress),
				lastBackendProgress: backendProgress
			});
			return;
		}

		if(headersConfirmedRef.current) {
			setIsUploading(true);
			setFlowPhase(UPLOAD_PHASES.VALIDATING_DATA, {
				lastBackendProgress: backendProgress
			});
			return;
		}

		setIsUploading(true);
		setFlowPhase(UPLOAD_PHASES.PREPARING_FILE, {
			lastBackendProgress: backendProgress
		});
	}, [setFlowPhase]);

	const handleHeadersSubmitted = useCallback((headers) => {
		awaitingFirstFeedbackRef.current = false;
		initialFeedbackRetryCountRef.current = 0;
		headersConfirmedRef.current = true;
		validationReviewedRef.current = false;
		hasSeenImportProgressRef.current = false;
		setHeadersConfirmed(true);
		setValidationReviewed(false);
		setHasSeenImportProgress(false);
		setShowHeadersFeedback(false);
		setShowUploadDataValidationFeedback(false);
		setFileHeaders([...headers]);
		fileHeadersRef.current = [...headers];
		multiScheduleCompletionHandledRef.current = false;
		setUploadState(null);
		setIsUploading(true);
		setCurrentScreen(3);
		setFlowPhase(UPLOAD_PHASES.VALIDATING_DATA, {
			percent: getSimulationBounds(UPLOAD_PHASES.VALIDATING_DATA).start
		});
	}, [setFlowPhase]);

	const handleValidationConfirmed = useCallback(() => {
		awaitingFirstFeedbackRef.current = false;
		initialFeedbackRetryCountRef.current = 0;
		validationReviewedRef.current = true;
		hasSeenImportProgressRef.current = false;
		setValidationReviewed(true);
		setHasSeenImportProgress(false);
		setShowUploadDataValidationFeedback(false);
		setUploadState(null);
		setIsUploading(true);
		setCurrentScreen(3);
		multiScheduleCompletionHandledRef.current = false;
		setScheduleProgress(buildInitialScheduleProgress(selectedSchedulesRef.current, schedules_from_current_schedule_year));
		setFlowPhase(UPLOAD_PHASES.IMPORTING_ROWS, {
			percent: 78,
			lastBackendProgress: 0
		});
	}, [schedules_from_current_schedule_year, setFlowPhase]);

	const handleCheckUpload = useCallback(async (process_type, file_upload, ignore_process_flags = false, onFinished = null, check_token = checkTokenRef.current) => {
		try {
			if(check_token !== checkTokenRef.current) return false;

			const running_processes = await file_upload.getSchoolProcesses(schoolInfo.current_schedule_year_id);
			if(check_token !== checkTokenRef.current) return false;

			const selected_schedule_id_set = new Set(selectedSchedulesRef.current.map(schedule_version_id => `${schedule_version_id}`));
			const current_schedule_is_selected = selected_schedule_id_set.size === 0 || selected_schedule_id_set.has(`${schoolInfo.current_schedule_version_id}`);
			const dv_process_index = running_processes.findIndex(process => process.process_type === process_type && process.data_version_id == schoolInfo.current_data_version_id);
			const current_sv_process_index = running_processes.findIndex(process => process.process_type === process_type && process.schedule_version_id == schoolInfo.current_schedule_version_id);
			const selected_sv_process_index = running_processes.findIndex(process => process.process_type === process_type && selected_schedule_id_set.has(`${process.schedule_version_id}`));
			const sv_process_index = (current_schedule_is_selected && current_sv_process_index !== -1) ? current_sv_process_index : selected_sv_process_index;

			const process_index = (sv_process_index !== -1) ? sv_process_index : ((dv_process_index !== -1) ? dv_process_index : null);
			const process_info = (process_index !== null) ? running_processes[process_index] : null;
			
			const progress_id = (process_info) ? process_info.progress_id : null;
			const failure = (process_info && !ignore_process_flags) ? process_info.failure : false;
			const is_done = (process_info && !ignore_process_flags) ? process_info.is_done : false;
			const terminal_failure_without_feedback = (process_info) ? (process_info.failure && process_info.is_done && !process_info.has_feedback) : false;
			const awaiting_initial_feedback = awaitingFirstFeedbackRef.current && !headersConfirmedRef.current && !validationReviewedRef.current && !hasSeenImportProgressRef.current;
			const scheduleInitialFeedbackRetry = () => {
				if(check_token !== checkTokenRef.current) return false;

				if(initialFeedbackRetryCountRef.current < INITIAL_FEEDBACK_MAX_RETRIES)
				{
					initialFeedbackRetryCountRef.current += 1;
					setCurrentScreen(3);
					setUploadComplete(false);
					setIsUploading(true);
					setUploadState(null);
					setFlowPhase(UPLOAD_PHASES.PREPARING_FILE, {
						percent: getSimulationBounds(UPLOAD_PHASES.PREPARING_FILE).start,
						lastBackendProgress: process_info?.progress ?? 0
					});

					if(initialFeedbackRetryTimeoutRef.current) {
						clearTimeout(initialFeedbackRetryTimeoutRef.current);
					}

					initialFeedbackRetryTimeoutRef.current = setTimeout(() => {
						initialFeedbackRetryTimeoutRef.current = null;
						handleCheckUpload(process_type, file_upload, true, onFinished, check_token);
					}, INITIAL_FEEDBACK_RETRY_DELAY_MS);
					return true;
				}

				setCurrentScreen(3);
				setUploadComplete(false);
				setIsUploading(false);
				setUploadState(null);
				setFlowPhase(UPLOAD_PHASES.WAITING_FOR_INITIAL_FEEDBACK, {
					percent: getPausedPercentForPhase(UPLOAD_PHASES.WAITING_FOR_INITIAL_FEEDBACK),
					lastBackendProgress: process_info?.progress ?? 0
				});
				return true;
			};

			if(progress_id === null || failure || is_done || (ignore_process_flags && terminal_failure_without_feedback)) 
			{
				if(awaiting_initial_feedback && !failure && !terminal_failure_without_feedback)
				{
					const retry_scheduled = scheduleInitialFeedbackRetry();
					if(retry_scheduled) return false;
				}

				const screen_to_show = (failure || terminal_failure_without_feedback) ? 'failure' : 1;
				if(screen_to_show === 'failure') {
					setFlowPhase(UPLOAD_PHASES.FAILURE, {percent: 0});
				}
				removeFile(screen_to_show);
				setCheckProcessProgressCompleted(true);
				return false;
			}

			setCurrentProgressID(progress_id);
			
			let feedback_checked = false;

			file_upload.cancelCheck();

			await file_upload.checkUploading(schoolInfo.school_id, progress_id, (event) => {
				if(check_token !== checkTokenRef.current) return;

				if(!event.has_started || event.done || event.has_feedback || event.failure) 
				{
					file_upload.cancelCheck();
				}
	
				if(event.failure && !event.has_feedback)
				{
					if(ignore_process_flags)
					{
						handleCheckUpload(process_type, file_upload, false, onFinished, check_token);
					}
					else
					{
						setFlowPhase(UPLOAD_PHASES.FAILURE, {percent: 0});
						removeFile('failure');
						setCheckProcessProgressCompleted(true);
					}
					return;
				}
				else if(event.failure && !ignore_process_flags)
				{
					setFlowPhase(UPLOAD_PHASES.FAILURE, {percent: 0});
					removeFile('failure');
					setCheckProcessProgressCompleted(true);
					return;
				}
				else if(!event.has_started)
				{
					if(awaiting_initial_feedback)
					{
						scheduleInitialFeedbackRetry();
						return;
					}

					removeFile(1);
					setCheckProcessProgressCompleted(true);
					return;
				}
				else if(event.has_started && !event.done && !event.has_feedback)
				{
					handleProcessingEvent(event);
				}
				else if(event.done)
				{
					if(awaiting_initial_feedback && !event.has_feedback)
					{
						scheduleInitialFeedbackRetry();
						return;
					}

					const watched_process_is_selected_schedule = process_info?.schedule_version_id && selectedSchedulesRef.current.map(schedule_version_id => `${schedule_version_id}`).includes(`${process_info.schedule_version_id}`);
					const waiting_for_multi_schedule_import = watched_process_is_selected_schedule && process_type === 'studentreqdata' && selectedSchedulesRef.current.length > 1 && (validationReviewedRef.current || hasSeenImportProgressRef.current) && !event.failure && !event.has_feedback;
					if(waiting_for_multi_schedule_import)
					{
						setIsUploading(true);
						setUploadComplete(false);
						setFlowPhase(UPLOAD_PHASES.IMPORTING_ROWS, {
							percent: getImportPercentFromBackend(event.progress ?? 1),
							lastBackendProgress: event.progress ?? 1
						});
						return;
					}

					setIsUploading(false);
					
					if(!event.failure)
					{
						setUploadComplete(true);
						setUploadState(null);
						setFlowPhase(UPLOAD_PHASES.SUCCESS, {
							percent: 100,
							lastBackendProgress: event.progress ?? 1
						});
	
						localStorage.removeItem('duplicates');
	
						if(!event.has_feedback) 
						{
							setCheckProcessProgressCompleted(true);
							if(onFinished) {
								onFinished();
							}
							else
							{
								removeFile();
							}
						}
					}
				}
	
				if(event.has_feedback && !feedback_checked)
				{
					feedback_checked = true; 
					
					const data = {school_id:schoolInfo.school_id, progress_id:progress_id, data_version_id:schoolInfo.current_data_version_id, process_type:process_type};
				
					getProcessFeedback(data).then(async (feedback_data) => {
						if(check_token !== checkTokenRef.current) return;
						setFeedbackData({...feedback_data});
						setCheckProcessProgressCompleted(true);

						if(feedback_data.data_type === 'upload_headers') 
						{
							awaitingFirstFeedbackRef.current = false;
							initialFeedbackRetryCountRef.current = 0;
							setCurrentScreen(3);
							setIsUploading(false);
							setShowHeadersFeedback(true);
							setUploadState('confirm_headers');
							setFlowPhase(UPLOAD_PHASES.AWAITING_HEADER_CONFIRMATION, {
								percent: getPausedPercentForPhase(UPLOAD_PHASES.AWAITING_HEADER_CONFIRMATION),
								lastBackendProgress: event.progress ?? 0
							});
						}
						else if(feedback_data.data_type === 'upload_validation') 
						{
							setCurrentScreen(3);

							if(feedback_data.data)  
							{
								const persisted_headers = (fileHeadersRef.current.length > 0) ? fileHeadersRef.current : ((localStorage.getItem('upload_headers') !== null) ? JSON.parse(localStorage.getItem('upload_headers')) : []);
								setFileHeaders([...persisted_headers]);
								fileHeadersRef.current = [...persisted_headers];

								setIsUploading(false);
								setUploadState('confirm_validation');
								setShowUploadDataValidationFeedback(true);
								setFlowPhase(UPLOAD_PHASES.AWAITING_VALIDATION_REVIEW, {
									percent: getPausedPercentForPhase(UPLOAD_PHASES.AWAITING_VALIDATION_REVIEW),
									lastBackendProgress: event.progress ?? 0
								});
							}
							else
							{
								const persisted_headers = (fileHeadersRef.current.length > 0) ? fileHeadersRef.current : ((localStorage.getItem('upload_headers') !== null) ? JSON.parse(localStorage.getItem('upload_headers')) : []);

								const selected_schedules = selectedSchedulesRef.current.join(',');
								const continue_data = {school_id:schoolInfo.school_id, progress_id:progress_id, data_version_id:schoolInfo.current_data_version_id, schedule_version_ids:selected_schedules, process_type:process_type, data:{headers:persisted_headers}};

								awaitingFirstFeedbackRef.current = false;
								initialFeedbackRetryCountRef.current = 0;
								validationReviewedRef.current = true;
								hasSeenImportProgressRef.current = false;
								multiScheduleCompletionHandledRef.current = false;
								setValidationReviewed(true);
								setHasSeenImportProgress(false);
								setIsUploading(true);
								setUploadState(null);
								setScheduleProgress(buildInitialScheduleProgress(selectedSchedulesRef.current, schedules_from_current_schedule_year));
								setFlowPhase(UPLOAD_PHASES.IMPORTING_ROWS, {
									percent: 78,
									lastBackendProgress: 0
								});

								await clearProcessFeedback(continue_data);
								await continueProcess(continue_data);
								handleCheckUpload(process_type, file_upload, true, onFinished, check_token);
							}
						}
					}).catch(() => {
						if(check_token !== checkTokenRef.current) return;
						setFlowPhase(UPLOAD_PHASES.FAILURE, {percent: 0});
						removeFile('failure');
						setCheckProcessProgressCompleted(true);
					});
				}
			});
		} catch (e)
		{
			if(check_token !== checkTokenRef.current) return false;
			file_upload.cancelCheck();
			setFlowPhase(UPLOAD_PHASES.FAILURE, {percent: 0});
			removeFile('failure');
			setCheckProcessProgressCompleted(true);
		}
	}, [
		handleProcessingEvent,
		removeFile,
		schoolInfo.school_id,
		schoolInfo.current_schedule_version_id,
		schoolInfo.current_data_version_id,
		schoolInfo.current_schedule_year_id,
		schedules_from_current_schedule_year,
		setFlowPhase
	]);
	
	const submitUpload = async (process_type) => {
		if(!file.name)
		{
			alert("You must upload a file!");
			return;
		}

		if(!isValidXlsxFile(file)) {
			setFile({name:null});
			setFileValidationError('Please upload an Excel .xlsx file. Other spreadsheet formats will not import correctly.');
			setCurrentScreen(1);
			return;
		}

		if(schedules_from_current_schedule_year.length > 1 && selectedSchedules.length === 0) {
			setFileValidationError('Please select at least one schedule for this upload.');
			return;
		}

		if(fileUpload) fileUpload.cancelCheck();

		const selected_schedules = selectedSchedules.join(',');
		const initial_schedule_progress = buildInitialScheduleProgress(selectedSchedules, schedules_from_current_schedule_year);

		const file_upload = new FileUpload(schoolInfo.current_data_version_id, selected_schedules, schoolInfo.school_id);

		setFileUpload(file_upload);
		setFileValidationError(null);
		setScheduleProgress(initial_schedule_progress);
		setIsUploading(true);
		setCurrentScreen(3);
		setUploadComplete(false);
		setUploadState(null);
		awaitingFirstFeedbackRef.current = true;
		multiScheduleCompletionHandledRef.current = false;
		initialFeedbackRetryCountRef.current = 0;
		headersConfirmedRef.current = false;
		validationReviewedRef.current = false;
		hasSeenImportProgressRef.current = false;
		setHeadersConfirmed(false);
		setValidationReviewed(false);
		setHasSeenImportProgress(false);
		setCheckProcessProgressCompleted(false);
		setFlowPhase(UPLOAD_PHASES.UPLOADING_FILE, {
			percent: 0,
			lastBackendProgress: 0
		});
		const check_token = checkTokenRef.current + 1;
		checkTokenRef.current = check_token;

		const is_replace = (uploadType === 'replace');

		const success = await file_upload.uploadFile(file, process_type, is_replace, (event) => {
			setUploadFlow(current => ({
				...buildUploadFlow(UPLOAD_PHASES.UPLOADING_FILE),
				percent: clampPercent((event.progress ?? 0) * 100),
				lastBackendProgress: event.progress ?? 0
			}));
		});

		setTimeout(function () {
			if(success)
			{
				setFlowPhase(UPLOAD_PHASES.PREPARING_FILE, {
					percent: getSimulationBounds(UPLOAD_PHASES.PREPARING_FILE).start,
					lastBackendProgress: 0
				});
				handleCheckUpload(process_type, file_upload, true, showSuccessScreen, check_token);
			} 
			else 
			{
				file_upload.cancelCheck();
				setIsUploading(false);
				setFlowPhase(UPLOAD_PHASES.FAILURE, {percent: 0});
				setCurrentScreen('failure');
			}
		}, 3000);
	};

	const toggleScheduleSelected = (schedule_version_id) => {
		const selected_index = selectedSchedules.findIndex(temp_schedule_version_id => temp_schedule_version_id === schedule_version_id);

		if(selected_index !== -1)
		{
			selectedSchedules.splice(selected_index, 1);
		}
		else 
		{
			selectedSchedules.push(schedule_version_id);
		}

		const next_selected_schedules = [...selectedSchedules];
		selectedSchedulesRef.current = next_selected_schedules;
		setSelectedSchedules(next_selected_schedules);
		setScheduleProgress(current => buildInitialScheduleProgress(next_selected_schedules, schedules_from_current_schedule_year, current));
		if(next_selected_schedules.length > 0) setFileValidationError(null);
	};

	const cancelUpload = () => {
		removeFile(1);
		setFileHeaders([]);
		setShowHeadersFeedback(false);
		setShowUploadDataValidationFeedback(false);
		setUploadState(null);

		const process_type = feedbackData?.process_type || uploadAPIName;
		if(!currentProgressID || !process_type) return;

		const data = {school_id:schoolInfo.school_id, progress_id:currentProgressID, data_version_id:schoolInfo.current_data_version_id, process_type:process_type, data:{cancel:true}};
		clearProcessFeedback(data);
		continueProcess(data);
	};

	const keepWaitingForInitialFeedback = useCallback(() => {
		if(!fileUploadRef.current) return;

		awaitingFirstFeedbackRef.current = true;
		initialFeedbackRetryCountRef.current = 0;
		setCurrentScreen(3);
		setUploadComplete(false);
		setUploadState(null);
		setIsUploading(true);
		setFlowPhase(UPLOAD_PHASES.PREPARING_FILE, {
			percent: getSimulationBounds(UPLOAD_PHASES.PREPARING_FILE).start,
			lastBackendProgress: uploadFlow.lastBackendProgress ?? 0
		});

		handleCheckUpload(uploadAPIName, fileUploadRef.current, true, showSuccessScreen, checkTokenRef.current);
	}, [handleCheckUpload, setFlowPhase, showSuccessScreen, uploadAPIName, uploadFlow.lastBackendProgress]);

	const tryUploadAgain = () => {
		if(!isCurrentAppScheduleYear || blockedByMissingSubscription)
		{
			removeFile(1);
			return;
		}

		cancelUpload();
	};

	const resetState = useCallback(() => {
		checkTokenRef.current += 1;
		setCurrentProgressID(null);
		setFile({name:null});
		setCheckProcessProgressCompleted(false);
		setIsUploading(false);
		setUploadComplete(false);
		setUploadState(null);
		setFeedbackData(null);
		setFileHeaders([]);
		setFileValidationError(null);
		setShowHeadersFeedback(false);
		setShowUploadDataValidationFeedback(false);
		localStorage.removeItem('upload_headers');
		fileHeadersRef.current = [];
		resetUploadFlowState();
	}, [resetUploadFlowState]);

	const handleCloseModal = () => {
		resetState();
		closeModal();
	};

	useEffect(() => {
		if(uploadFlow.percentMode !== 'simulated') return;

		const {start, end} = getSimulationBounds(uploadFlow.phase);

		setUploadFlow(current => {
			if(current.phase !== uploadFlow.phase || current.percentMode !== 'simulated') return current;

			const normalized_percent = Math.max(current.percent, start);
			if(normalized_percent === current.percent) return current;

			return {...current, percent: normalized_percent};
		});

		const interval = setInterval(() => {
			setUploadFlow(current => {
				if(current.phase !== uploadFlow.phase || current.percentMode !== 'simulated') return current;
				if(current.percent >= end) return current;

				const increment = current.percent < (start + 8) ? 2 : 1;
				return {
					...current,
					percent: Math.min(end, current.percent + increment)
				};
			});
		}, 700);

		return () => clearInterval(interval);
	}, [uploadFlow.phase, uploadFlow.percentMode]);

	useEffect(() => {
		setHeartbeatTick(0);
	}, [uploadFlow.phase]);

	useEffect(() => {
		const active_long_running_phase = [
			UPLOAD_PHASES.PREPARING_FILE,
			UPLOAD_PHASES.VALIDATING_DATA,
			UPLOAD_PHASES.IMPORTING_ROWS
		].includes(uploadFlow.phase);

		if(currentScreen !== 3 || !isUploading || !active_long_running_phase) return;

		const interval = setInterval(() => {
			setHeartbeatTick(current => current + 1);
		}, 15000);

		return () => clearInterval(interval);
	}, [currentScreen, isUploading, uploadFlow.phase]);

	useEffect(() => {
		if(!show_multi_schedule_progress || uploadFlow.phase !== UPLOAD_PHASES.IMPORTING_ROWS || !fileUploadRef.current) return;

		let poll_cancelled = false;

		const pollScheduleProgress = async () => {
			try {
				const running_processes = await fileUploadRef.current.getSchoolProcesses(schoolInfo.current_schedule_year_id);
				if(poll_cancelled) return;

				const selected_schedule_set = new Set(selectedSchedulesRef.current.map(schedule_version_id => `${schedule_version_id}`));
				const matching_processes = running_processes.filter(process => (
					process.process_type === uploadAPIName &&
					selected_schedule_set.has(`${process.schedule_version_id}`)
				));
				const matching_process_by_schedule_id = matching_processes.reduce((results, process) => {
					results[process.schedule_version_id] = process;
					return results;
				}, {});

				setScheduleProgress(current => {
					const next_progress = buildInitialScheduleProgress(selectedSchedulesRef.current, schedules_from_current_schedule_year, current);

					matching_processes.forEach(process => {
						const schedule_version_id = process.schedule_version_id;
						next_progress[schedule_version_id] = getScheduleProgressFromProcess(process, next_progress[schedule_version_id] || {});
					});

					return next_progress;
				});

				const selected_schedule_ids = selectedSchedulesRef.current.map(schedule_version_id => `${schedule_version_id}`);
				const all_selected_processes_found = selected_schedule_ids.length > 0 && selected_schedule_ids.every(schedule_version_id => matching_process_by_schedule_id[schedule_version_id]);
				const all_selected_processes_done = all_selected_processes_found && selected_schedule_ids.every(schedule_version_id => isTruthyProcessFlag(matching_process_by_schedule_id[schedule_version_id].is_done));
				const any_selected_process_failed = matching_processes.some(process => isTruthyProcessFlag(process.failure));

				if(any_selected_process_failed)
				{
					setIsUploading(false);
					setFlowPhase(UPLOAD_PHASES.FAILURE, {percent: uploadFlow.percent});
					return;
				}

				if(all_selected_processes_done && !multiScheduleCompletionHandledRef.current)
				{
					multiScheduleCompletionHandledRef.current = true;
					setIsUploading(false);
					setUploadComplete(true);
					setCheckProcessProgressCompleted(true);
					setFlowPhase(UPLOAD_PHASES.SUCCESS, {
						percent: 100,
						lastBackendProgress: 1
					});
					showSuccessScreen();
				}
			} catch(e) {
				if(poll_cancelled) return;
				setScheduleProgress(current => {
					const next_progress = buildInitialScheduleProgress(selectedSchedulesRef.current, schedules_from_current_schedule_year, current);

					Object.keys(next_progress).forEach(schedule_version_id => {
						if(next_progress[schedule_version_id].status === 'queued') {
							next_progress[schedule_version_id] = {
								...next_progress[schedule_version_id],
								statusLabel: 'Waiting for status'
							};
						}
					});

					return next_progress;
				});
			}
		};

		pollScheduleProgress();
		const interval = setInterval(pollScheduleProgress, 5000);

		return () => {
			poll_cancelled = true;
			clearInterval(interval);
		};
	}, [show_multi_schedule_progress, uploadFlow.phase, uploadFlow.percent, uploadAPIName, schoolInfo.current_schedule_year_id, selected_upload_schedules_string, showSuccessScreen, setFlowPhase]);

	useEffect(() => {
		selectedSchedulesRef.current = selectedSchedules;
	}, [selectedSchedules]);

	useEffect(() => {
		fileHeadersRef.current = fileHeaders;
	}, [fileHeaders]);

	useEffect(() => {
		headersConfirmedRef.current = headersConfirmed;
	}, [headersConfirmed]);

	useEffect(() => {
		validationReviewedRef.current = validationReviewed;
	}, [validationReviewed]);

	useEffect(() => {
		hasSeenImportProgressRef.current = hasSeenImportProgress;
	}, [hasSeenImportProgress]);

	useEffect(() => {
		fileUploadRef.current = fileUpload;
	}, [fileUpload]);

	useEffect(() => {
		handleCheckUploadRef.current = handleCheckUpload;
	}, [handleCheckUpload]);

	//////////////////////
	///// USE EFFECT /////
	//////////////////////
	useEffect(() => {
		if(fileUploadRef.current) fileUploadRef.current.cancelCheck();

		selectedSchedulesRef.current = schedule_version_ids_from_current_schedule_year;
		setSelectedSchedules(current => {
			if(current.length === schedule_version_ids_from_current_schedule_year.length && current.every((schedule_version_id, index) => schedule_version_id === schedule_version_ids_from_current_schedule_year[index])) {
				return current;
			}

			return [...schedule_version_ids_from_current_schedule_year];
		});
		setScheduleProgress(current => buildInitialScheduleProgress(schedule_version_ids_from_current_schedule_year, schedules_from_current_schedule_year, current));

		const file_upload = new FileUpload(schoolInfo.current_data_version_id, selected_schedules_string, schoolInfo.school_id);
		setFileUpload(file_upload);
		fileUploadRef.current = file_upload;

		if(!isCurrentAppScheduleYear || blockedByMissingSubscription)
		{
			setCheckProcessProgressCompleted(true);
			return;
		}

		if(handleCheckUploadRef.current) {
			const check_token = checkTokenRef.current + 1;
			checkTokenRef.current = check_token;
			handleCheckUploadRef.current(uploadAPIName, file_upload, false, null, check_token);
		}
	}, [isCurrentAppScheduleYear, blockedByMissingSubscription, schoolInfo.school_id, schoolInfo.current_data_version_id, selected_schedules_string, uploadAPIName]);
	const display_file_name = getDisplayFileName(file, uploadTitle);
	const stepper_steps = getStepperSteps(uploadFlow.phase);
	const show_progress_card = currentScreen === 3;
	const allow_modal_close = !isUploading && uploadFlow.phase !== UPLOAD_PHASES.WAITING_FOR_INITIAL_FEEDBACK;
	const upload_status_note = getUploadStatusNote(uploadFlow, heartbeatTick);
	const progress_fill_classes = [
		'upload-progress-fill',
		`upload-progress-fill-${uploadFlow.percentMode}`,
		(isUploading && uploadFlow.percentMode !== 'paused' && uploadFlow.phase !== UPLOAD_PHASES.SUCCESS && uploadFlow.phase !== UPLOAD_PHASES.FAILURE) ? 'upload-progress-fill-working' : ''
	].filter(Boolean).join(' ');
	let modal_body = null;

	if(!isCurrentAppScheduleYear)
	{
		modal_body = <div>Uploads are not allowed in previous scheduling years.</div>;
	}
	else if(blockedByMissingSubscription)
	{
		modal_body = <div>You need a paid subscription for this scheduling year to upload {uploadTitle.toLowerCase()}.</div>;
	}
	else if(currentScreen === 1)
	{
		modal_body = (
			<>
			{!checkProcessProgressCompleted ?
				(
					<div className='center-text'>
						<img src={require("../images/balls.gif")} alt='loading...' style={{height:'40px'}}/>
						<div className='medium-text'>Checking status...</div>
					</div>
				):
				(
					<div className="upload_type_container">
						{fileValidationError &&
							<div className='upload-file-error-message'>{fileValidationError}</div>
						}
						<div className="drag_file_upload_container">
							<FontAwesomeIcon className='upload-icon' icon={faFileUpload}/>
							<div>Click here to select an Excel (.xlsx) file</div>
							<div className='small-text' style={{margin:'5px 0px 0px 0px'}}>(you can also drag in your files)</div>
							<input type="file" className='upload_data' accept='.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' onChange={onFileChange}/>
						</div>
					</div>
				)
			}
			</>
		);
	}
	else if(currentScreen === 2)
	{
		modal_body = (
			<div>
				{fileValidationError &&
					<div className='upload-file-error-message'>{fileValidationError}</div>
				}
				<div className='uploaded-file-container'>
					<div className='uploaded-file-icon-container'>
						<img src={require('../images/excel.png')} style={{height:'30px'}} alt='excel icon'/>
					</div>
					<div className='uploaded-file-name-container'>
						<div className="teachers_file">{file.name}</div>
					</div>
					<div className='uploaded-file-remove-file' onClick={() => removeFile(1)}><FontAwesomeIcon className='remove-file-icon' icon={faTimesCircle}/></div>
				</div>
				{addReplaceBool &&
					<>
					<h4 className='upload-file-subtitle'>Select your upload type:</h4>
					<div className='upload-file-upload-type-container' onClick={() => setUploadType('add')}>
						<input className='upload-file-upload-type-radio' type='radio' checked={uploadType === 'add'} readOnly />
						<div>
							<h3 className='upload-file-upload-type-title'>Add Data</h3>
							<div>Choose this if you want to add new data from your file to existing data. Any existing data will not be affected.</div>
						</div>
					</div>
					<div className='upload-file-upload-type-container' onClick={() => setUploadType('replace')}>
						<input className='upload-file-upload-type-radio' type='radio' checked={uploadType === 'replace'} readOnly />
						<div>
							<h3 className='upload-file-upload-type-title'>Replace Data</h3>
							<div>Choose this if you want to replace all existing data with the data from your file.</div>
						</div>
					</div>
					</>
				}
				{(schedules_from_current_schedule_year.length > 1) &&
					<>
					<h4 className='upload-file-subtitle'>Select the schedules you would like this upload to change:</h4>
					{schedules_from_current_schedule_year.map((schedule, i) => {
						const schedule_version_id = schedule.schedule_version_id;
						const schedule_name = schedule.schedule_name;
						const schedule_date = schedule.time_stamp;
						const schedule_selected = selectedSchedules.findIndex(selected_schedule_version_id => selected_schedule_version_id === schedule_version_id) !== -1;

						return (
							<div className='upload-file-schedule-select-container' key={i} onClick={()=>toggleScheduleSelected(schedule_version_id)}>
								{schedule_selected ?
									(
										<FontAwesomeIcon className='upload-file-schedule-select-box fas-checkbox-checked' icon={faCheckSquare}/>
									):
									(
										<FontAwesomeIcon className='upload-file-schedule-select-box fas-checkbox-unchecked dark-gray-text' icon={faSquare}/>
									)
								}
								<div>
									<div>{schedule_name}</div>
									<div className='small-text'>{schedule_date}</div>
								</div>
							</div>
						);
					})}
					</>
				}
				<span className='btn blue-btn btn-large upload-file-btn' onClick={()=>submitUpload(uploadAPIName)}>Start Uploading</span>
			</div>
		);
	}
	else if(show_progress_card)
	{
		modal_body = (
			<div className='upload-progress-card'>
				<div className='upload-progress-card-top'>
					<div className='upload-progress-file'>
						<div className='uploaded-file-icon-container'>
							<img src={require('../images/excel.png')} style={{height:'30px'}} alt='excel icon'/>
						</div>
						<div className='upload-progress-file-copy'>
							<div className='teachers_file'>{display_file_name}</div>
							<div className='upload-progress-file-type'>Excel file</div>
						</div>
					</div>
					<div className='upload-progress-percent'>{uploadFlow.percent}%</div>
				</div>

				<div className='upload-progress-title'>{uploadFlow.label}</div>
				<div className='upload-progress-detail'>{uploadFlow.detail}</div>

				<div className='upload-progress-track'>
					<div className={progress_fill_classes} style={{width: `${uploadFlow.percent}%`}}></div>
				</div>

				<div className='upload-progress-meta'>
					<div className='upload-progress-status'>{getFlowStatusMessage(uploadFlow)}</div>
					<div className='upload-progress-status-note'>{upload_status_note}</div>
				</div>

				{show_multi_schedule_progress && uploadFlow.phase === UPLOAD_PHASES.IMPORTING_ROWS &&
					<div className='upload-schedule-progress-list'>
						<div className='upload-schedule-progress-heading'>Schedule version progress</div>
						{schedule_progress_rows.map(schedule_progress => (
							<div className={`upload-schedule-progress-row upload-schedule-progress-row-${schedule_progress.status}`} key={schedule_progress.schedule_version_id}>
								<div className='upload-schedule-progress-main'>
									<div className='upload-schedule-progress-name'>{schedule_progress.schedule_name}</div>
									{schedule_progress.schedule_date &&
										<div className='upload-schedule-progress-date'>{schedule_progress.schedule_date}</div>
									}
								</div>
								<div className='upload-schedule-progress-meter'>
									<div className='upload-schedule-progress-track'>
										<div className='upload-schedule-progress-fill' style={{width: `${schedule_progress.percent}%`}}></div>
									</div>
									<div className='upload-schedule-progress-status'>{schedule_progress.statusLabel}</div>
								</div>
							</div>
						))}
					</div>
				}

				<div className='upload-stepper'>
					{stepper_steps.map((step, index) => (
						<div className={`upload-step upload-step-${step.status}`} key={`${step.label}-${index}`}>
							<div className='upload-step-badge'>
								{step.status === 'complete' ? <FontAwesomeIcon icon={faCheck}/> : index + 1}
							</div>
							<div className='upload-step-label'>{step.label}</div>
						</div>
					))}
				</div>

				<div className='upload-progress-actions'>
					{uploadFlow.actionLabel &&
						<div className='btn blue-btn' onClick={openPausedAction}>{uploadFlow.actionLabel}</div>
					}
					{uploadFlow.phase === UPLOAD_PHASES.WAITING_FOR_INITIAL_FEEDBACK &&
						<>
							<div className='btn blue-btn' onClick={keepWaitingForInitialFeedback}>Keep Waiting</div>
							<div className='btn white-btn' onClick={cancelUpload}>Cancel Upload</div>
						</>
					}
					{uploadComplete && !uploadFlow.canCancel &&
						<div className='upload-complete-icon-container'>
							<FontAwesomeIcon className='upload-complete-icon green-text' icon={faCheck}/>
						</div>
					}
				</div>
			</div>
		);
	}
	else if(currentScreen === 4)
	{
		modal_body = (
			<div className='text-align-center'>
				<FontAwesomeIcon icon={faCheckCircle} className='green-text' style={{fontSize:'64px',marginTop:'20px'}}/>
				<h1>Success!</h1>
				<p>You have successfully uploaded your data!</p>
			</div>
		);
	}
	else if(currentScreen === 'failure')
	{
		modal_body = (
			<div className='text-align-center'>
				<FontAwesomeIcon icon={faTriangleExclamation} className='red-text' style={{fontSize:'64px',marginTop:'20px'}}/>
				<h1>Error</h1>
				<p>Something went wrong. Please make sure your file is in .xlsx format and try again!</p>
				<div className='blue-link text-align-center full-width' onClick={tryUploadAgain}>Try Upload Again</div>
			</div>
		);
	}

	return (
		<>
		<div className='modal'>
			<div id='upload-modal-content' className='modal-content'>
				{allow_modal_close && <span className="modal-close-right" onClick={handleCloseModal}>×</span>}
				<h1 id='upload-modal-title'>Upload {uploadTitle}</h1>
				{modal_body}
			</div>
		</div>
		{showHeadersFeedback &&
			<UploadEditHeaders
				schoolInfo={schoolInfo}
				progressID={currentProgressID}
				selectedSchedules={selectedSchedules}
				scheduleInfo={scheduleInfo}
				feedbackData={feedbackData}
				setFeedbackData={setFeedbackData}
				setFileHeaders={setFileHeaders}
				setShowHeadersFeedback={setShowHeadersFeedback}
				fileUpload={fileUpload}
				setUploadState={setUploadState}
				cancelUpload={cancelUpload}
				handleCheckUpload={handleCheckUpload}
				onHeadersSubmitted={handleHeadersSubmitted}
				onComplete={showSuccessScreen}
			/>
		}
		{showUploadDataValidationFeedback &&
			<UploadConfirmDataValidation
				schoolInfo={schoolInfo}
				progressID={currentProgressID}
				selectedSchedules={selectedSchedules}
				scheduleInfo={scheduleInfo}
				feedbackData={feedbackData}
				setFeedbackData={setFeedbackData}
				fileHeaders={fileHeaders}
				setShowUploadDataValidationFeedback={setShowUploadDataValidationFeedback}
				fileUpload={fileUpload}
				setUploadState={setUploadState}
				cancelUpload={cancelUpload}
				handleCheckUpload={handleCheckUpload}
				onValidationConfirmed={handleValidationConfirmed}
				onComplete={showSuccessScreen}
			/>
		}
		</>
	);
}
